import React from "react";
import { X, QrCode, Printer, Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Royal Mail label fulfilment modal (Iteration 196).
 *
 * Presents the seller with TWO clear ways to send the parcel once the
 * Click & Drop label has been generated:
 *
 *   Option A — Digital QR code (no printer). Scanned at any Post
 *              Office / RM Customer Service Point where staff print
 *              the label for free.
 *
 *   Option B — Print-at-home PDF. Opens the browser print dialog with
 *              4x6 / A4 optimised styles.
 */
export default function RoyalMailLabelModal({ open, onClose, labelInfo, orderId }) {
  const [copied, setCopied] = React.useState(false);
  const qrUrl = labelInfo?.qr_code_url;
  const labelUrl = labelInfo?.label_url;
  const tracking = labelInfo?.tracking_number || labelInfo?.qr_payload;

  if (!open) return null;

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `royal-mail-qr-${tracking || orderId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR code saved to your device");
  };

  const openPdfWindow = () => {
    if (!labelUrl) return;
    // Data URIs can't be opened via window.open in some browsers; use
    // an <a target=_blank> click instead so mobile Safari cooperates.
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (w) {
      w.document.write(
        `<html><head><title>Royal Mail shipping label · ${tracking || ""}</title>` +
          `<style>@media print{@page{margin:8mm;size:auto}body{margin:0}}` +
          `body{margin:0;background:#111;display:flex;align-items:center;justify-content:center}` +
          `iframe{border:0;width:100vw;height:100vh}` +
          `.bar{position:fixed;top:12px;right:12px;z-index:10}` +
          `.bar button{background:#FF85A1;color:#fff;border:0;padding:10px 18px;border-radius:9999px;font-weight:700;cursor:pointer}` +
          `</style></head><body>` +
          `<div class="bar"><button onclick="window.print()">Print label</button></div>` +
          `<iframe src="${labelUrl}"></iframe></body></html>`
      );
      w.document.close();
    } else {
      window.location.href = labelUrl;
    }
  };

  const copyTracking = async () => {
    if (!tracking) return;
    try {
      await navigator.clipboard.writeText(tracking);
      setCopied(true);
      toast.success("Tracking number copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="royal-mail-label-modal"
    >
      <div
        className="w-full max-w-2xl bg-white rounded-3xl shadow-xl overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold text-brandTeal uppercase tracking-widest mb-1">Royal Mail Label Ready</div>
            <h2 className="font-display text-2xl text-slate-900">How would you like to send it?</h2>
            {tracking && (
              <button
                type="button"
                onClick={copyTracking}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 font-mono"
                data-testid="royal-mail-copy-tracking"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                Tracking: {tracking}
              </button>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100"
            data-testid="royal-mail-modal-close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 p-5">
          {/* Option A — QR code. Only render when we have a real
              tracking-encoded QR from the API. Otherwise the whole
              tile hides — no placeholder card that looks like a stub. */}
          {qrUrl ? (
            <section
              className="rounded-2xl border border-slate-200 p-5 bg-slate-50/40 flex flex-col"
              data-testid="royal-mail-option-qr"
            >
              <div className="inline-flex items-center gap-2 text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">
                <QrCode className="w-4 h-4" /> Option A · No printer
              </div>
              <h3 className="font-display text-lg text-slate-900 mb-2">Digital QR code</h3>
              <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-center mb-3">
                <img
                  src={qrUrl}
                  alt="Royal Mail QR code"
                  className="w-52 h-52"
                  data-testid="royal-mail-qr-image"
                />
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Show this QR code at any Post Office or Royal Mail Customer Service Point. They will print your shipping label for free!
              </p>
              <button
                type="button"
                onClick={downloadQr}
                className="btn-primary w-full mt-auto"
                data-testid="royal-mail-download-qr"
              >
                <Download className="w-4 h-4" /> Download QR Code to Phone
              </button>
            </section>
          ) : null}

          {/* Option B — Official Royal Mail PDF (data URI direct from
              Click & Drop). Only render when we actually have a real
              label_url. */}
          {labelUrl ? (
            <section
              className="rounded-2xl border border-slate-200 p-5 bg-slate-50/40 flex flex-col"
              data-testid="royal-mail-option-pdf"
            >
              <div className="inline-flex items-center gap-2 text-xs font-bold text-brandTeal uppercase tracking-wider mb-2">
                <Printer className="w-4 h-4" /> Option B · Print at home
              </div>
              <h3 className="font-display text-lg text-slate-900 mb-2">Official Royal Mail PDF</h3>
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-3 aspect-[3/4]">
                <iframe
                  src={labelUrl}
                  title="Royal Mail shipping label"
                  className="w-full h-full"
                  data-testid="royal-mail-pdf-preview"
                />
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Prints crisply on A4 or 4×6 thermal labels. Attach to the parcel and drop into any Royal Mail post box.
              </p>
              <div className="flex gap-2 mt-auto">
                <a
                  href={labelUrl}
                  download={`royal-mail-label-${tracking || orderId}.pdf`}
                  className="btn-secondary flex-1 justify-center"
                  data-testid="royal-mail-download-pdf"
                >
                  <Download className="w-4 h-4" /> Save PDF
                </a>
                <button
                  type="button"
                  onClick={openPdfWindow}
                  className="btn-primary flex-1"
                  data-testid="royal-mail-print-pdf"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
              </div>
            </section>
          ) : null}

          {!qrUrl && !labelUrl && (
            <div
              className="col-span-full rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900"
              data-testid="royal-mail-not-ready"
            >
              <div className="font-bold mb-1">Label not ready yet</div>
              <p className="mb-3">
                The Royal Mail Click &amp; Drop API hasn&rsquo;t returned this label yet. This usually means the token isn&rsquo;t configured in the current environment or the API rejected the request.
              </p>
              <p className="text-xs text-amber-800">
                If you&rsquo;re on production and this persists, please contact support &mdash; do not use this modal to send a parcel until the real label appears.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
